import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  FileText,
  UploadCloud,
  CheckCircle2,
  AlertCircle,
  Clock,
  ArrowLeft,
  ShieldCheck,
  Eye,
  RefreshCw,
  ExternalLink,
  X,
} from "lucide-react";
import {
  fetchUserDocuments,
  uploadPartnerDocument,
  fetchPartnerUserById,
} from "../../api/partner/partnerApi";
import {
  setUploadedDocuments,
  addUploadedDocument,
} from "../../features/partnerOnboarding/partnerOnboardingSlice";
import {
  DOCUMENT_TITLES,
  getRequiredDocuments,
  checkDocumentsStatus,
} from "../../guards/partnerGuard";
import { showToast } from "../../components/common/notification/Notification";
import "../../components/partner/PartnerGuardModal.css";
import "./PartnerPages.css";

export const PartnerDocuments = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authUser = useSelector((state) => state.auth.user);
  const token = authUser?.accessToken || authUser?.token || authUser?.refreshToken;
  const userId = authUser?.user?.id || authUser?.userDetails?.id;

  const [partnerDetails, setPartnerDetails] = useState(authUser?.user || authUser?.userDetails || null);
  const [documentsList, setDocumentsList] = useState([]);
  const [assetUrl, setAssetUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploadingDocType, setUploadingDocType] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);

  const serviceId = String(partnerDetails?.service_id || partnerDetails?.service?.id || "1");
  const requiredDocs = getRequiredDocuments(serviceId);

  const loadVerificationData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [userRes, docsRes] = await Promise.allSettled([
        userId ? fetchPartnerUserById(token, userId) : Promise.resolve(null),
        fetchUserDocuments(token),
      ]);

      let userObj = null;
      if (userRes.status === "fulfilled" && userRes.value) {
        userObj = userRes.value?.user || userRes.value?.data?.user || userRes.value?.data || null;
        setPartnerDetails(userObj);
        setAssetUrl(userRes.value?.assetUrl || userRes.value?.data?.assetUrl || "");
      }

      if (docsRes.status === "fulfilled") {
        const docs = docsRes.value?.documents || docsRes.value?.data || userObj?.documents || [];
        setDocumentsList(docs);
        dispatch(setUploadedDocuments(docs));
      }
    } catch (err) {
      console.warn("Error loading document verification data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVerificationData();
  }, [token, userId]);

  const docStatus = checkDocumentsStatus(partnerDetails, documentsList);

  const handleFileUpload = async (docKey, file) => {
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      showToast("File size exceeds maximum limit of 10 MB.", { type: "error" });
      return;
    }

    setUploadingDocType(docKey);
    try {
      const response = await uploadPartnerDocument(token, userId, docKey, file);

      if (response?.statusType === "SUCCESS" || response?.status === "SUCCESS") {
        showToast(`${DOCUMENT_TITLES[docKey] || docKey.toUpperCase()} uploaded successfully!`, {
          type: "success",
        });
        dispatch(
          addUploadedDocument({
            name: docKey,
            document_type: docKey,
            file_url: response.document?.file_url || "",
          })
        );
        loadVerificationData();
      } else {
        const msg = response?.text || response?.message || "Document upload failed.";
        showToast(msg, { type: "error" });
      }
    } catch (err) {
      console.error("Document upload error:", err);
      showToast("Failed to upload document. Please try again.", { type: "error" });
    } finally {
      setUploadingDocType(null);
    }
  };

  const getDocPreviewUrl = (doc) => {
    if (!doc) return "";
    const rawUrl = doc.document_url || doc.file_url || doc.url || "";
    if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://")) {
      return rawUrl;
    }
    const cleanAsset = (assetUrl || "").replace(/\/+$/, "");
    const cleanPath = rawUrl.replace(/^\/+/, "");
    return `${cleanAsset}/${cleanPath}`;
  };

  const isVerified = Boolean(
    partnerDetails?.active_status ||
    partnerDetails?.is_verified ||
    partnerDetails?.status === "VERIFIED"
  );

  const overallStatus = isVerified
    ? "VERIFIED"
    : docStatus.isComplete
    ? "UNDER_REVIEW"
    : "PENDING";

  return (
    <div className="partner-verification-page">
      <div className="partner-verification-page__container">
        {/* Navigation Breadcrumb */}
        <Link to="/partner/workspace" className="partner-back-btn">
          <ArrowLeft size={16} />
          <span>Back to Partner Workspace</span>
        </Link>

        {/* Overview Banner */}
        <div className="partner-verification-overview">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
            <div>
              <h1 className="partner-dash__title mb-1">Partner Document Verification</h1>
              <p className="partner-dash__subtitle">
                Upload at least one government-issued KYC document (Aadhaar Card, PAN Card{serviceId === "2" ? ", or RERA Certificate" : ""}). Only one document is required to complete verification.
              </p>
            </div>
            <button
              type="button"
              className="partner-btn partner-btn--outline d-flex align-items-center gap-2"
              onClick={loadVerificationData}
              disabled={loading}
            >
              <RefreshCw size={14} className={loading ? "partner-spin" : ""} />
              <span>{loading ? "Refreshing..." : "Refresh Status"}</span>
            </button>
          </div>

          <div className="d-flex align-items-center gap-3 pt-3 border-top">
            <span className="text-muted font-weight-bold" style={{ fontSize: "0.875rem" }}>
              Status:
            </span>
            {overallStatus === "VERIFIED" && (
              <span className="partner-chip partner-chip--success">
                <CheckCircle2 size={13} /> Account Verified ✓
              </span>
            )}
            {overallStatus !== "VERIFIED" && docStatus.isComplete && (
              <span className="partner-chip partner-chip--info">
                <Clock size={13} /> KYC Document Submitted ✓ (Under Review)
              </span>
            )}
            {!docStatus.isComplete && (
              <span className="partner-chip partner-chip--warning">
                <AlertCircle size={13} /> 1 Document Required (Upload Aadhaar, PAN{serviceId === "2" ? " or RERA" : ""})
              </span>
            )}
          </div>
        </div>

        {/* Upload Cards Grid */}
        <div className="partner-card mb-4">
          <h3 className="partner-card__title mb-2">Partner KYC Documents</h3>
          <p className="partner-card__subtitle mb-4">
            Upload at least one of the documents below. Only one document is mandatory; any additional document is optional.
          </p>

          <div className="row g-4">
            {requiredDocs.map((docKey) => {
              const uploadedDoc = docStatus.uploadedDocsMap[docKey];
              const isUploaded = Boolean(uploadedDoc);
              const title = DOCUMENT_TITLES[docKey] || docKey.toUpperCase();
              const isUploading = uploadingDocType === docKey;

              return (
                <div key={docKey} className="col-12 col-md-6 col-lg-4">
                  <div
                    className={`partner-doc-upload-item h-100 flex-column align-items-start ${
                      isUploaded ? "partner-doc-upload-item--review" : ""
                    }`}
                  >
                    <div className="d-flex align-items-center gap-2 mb-2 w-100 justify-content-between">
                      <div className="d-flex align-items-center gap-2">
                        <div className="partner-doc-upload-item__icon">
                          <FileText size={20} />
                        </div>
                        <div>
                          <h4 className="partner-doc-upload-item__title">{title}</h4>
                        </div>
                      </div>
                      {isUploaded && (
                        <span className="partner-chip partner-chip--success">Uploaded ✓</span>
                      )}
                      {!isUploaded && docStatus.isComplete && (
                        <span className="badge bg-light text-muted border">Optional</span>
                      )}
                      {!isUploaded && !docStatus.isComplete && (
                        <span className="badge bg-warning bg-opacity-10 text-dark border border-warning">Required (1 of {requiredDocs.length})</span>
                      )}
                    </div>

                    <p className="partner-doc-upload-item__desc mb-3">
                      {docKey === "reraregistration"
                        ? "State Real Estate Regulatory Authority certificate scan."
                        : docKey === "pan"
                        ? "Personal or company Permanent Account Number card."
                        : "Government-issued Aadhaar identity card."}
                    </p>

                    <div className="mt-auto w-100 d-flex flex-column gap-2">
                      {isUploaded && (
                        <button
                          type="button"
                          className="partner-btn partner-btn--outline w-100 d-flex align-items-center justify-content-center gap-2"
                          onClick={() =>
                            setViewingDoc({
                              title,
                              url: getDocPreviewUrl(uploadedDoc),
                            })
                          }
                        >
                          <Eye size={15} />
                          <span>View Uploaded Document</span>
                        </button>
                      )}

                      <label
                        htmlFor={`doc-upload-${docKey}`}
                        className={`partner-btn w-100 d-flex align-items-center justify-content-center gap-2 ${
                          isUploaded ? "partner-btn--outline" : "partner-btn--primary"
                        } ${isUploading ? "disabled" : ""}`}
                        style={{ cursor: isUploading ? "wait" : "pointer" }}
                      >
                        <UploadCloud size={16} />
                        <span>
                          {isUploading
                            ? `Uploading ${title}...`
                            : isUploaded
                            ? `Re-upload ${title}`
                            : `Upload ${title}`}
                        </span>
                      </label>
                      <input
                        id={`doc-upload-${docKey}`}
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.webp"
                        className="d-none"
                        disabled={isUploading}
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            handleFileUpload(docKey, e.target.files[0]);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 pt-3 border-top">
          <Link to="/partner/workspace" className="partner-btn partner-btn--outline">
            <ArrowLeft size={16} />
            <span>Return to Partner Workspace</span>
          </Link>
          <div className="text-muted" style={{ fontSize: "0.8125rem" }}>
            Documents are reviewed by the Super Admin team within 24 to 48 business hours.
          </div>
        </div>
      </div>

      {/* Document Viewer Modal */}
      {viewingDoc && (
        <div className="partner-doc-viewer-modal" onClick={() => setViewingDoc(null)}>
          <div className="partner-doc-viewer-card" onClick={(e) => e.stopPropagation()}>
            <div className="partner-doc-viewer-header">
              <span className="fw-bold text-dark">{viewingDoc.title}</span>
              <div className="d-flex align-items-center gap-2">
                <a
                  href={viewingDoc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                >
                  <ExternalLink size={14} />
                  <span>Open Full Screen</span>
                </a>
                <button
                  type="button"
                  className="btn btn-sm btn-light p-1"
                  onClick={() => setViewingDoc(null)}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="partner-doc-viewer-body">
              {viewingDoc.url.toLowerCase().endsWith(".pdf") ? (
                <iframe src={`${viewingDoc.url}#toolbar=0`} title={viewingDoc.title} />
              ) : (
                <img src={viewingDoc.url} alt={viewingDoc.title} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerDocuments;
